import { useEffect, useState } from 'react';
import { ArrowLeft, ChevronRight, Copy, KeyRound, RefreshCw, Trash2 } from 'lucide-react';
import { orderedCategories } from '@milk/shared';
import { addCategory, deleteCategory, syncNow, updateCategory, useStore } from '../store/store';
import { api } from '../lib/api';
import { webOrigin } from '../lib/config';
import { linkProps } from '../lib/router';
import { autoSwitchEnabled, setAutoSwitch } from '../lib/presence';
import { defaultPasskeyName, passkeysSupported, registerPasskey, wasCancelled } from '../lib/passkeys';
import { copyText, formatDate, Spinner, useLoad, useSession, useToast } from '../components/ui';

export function Settings() {
  const { user } = useSession();
  return (
    <>
      <div className="page-head">
        <a className="icon-btn" {...linkProps('/')} aria-label="Back to the list"><ArrowLeft size={22} aria-hidden /></a>
        <h1>Settings</h1>
      </div>
      <Shopping />
      <Sections />
      <Account />
      <Passkeys />
      {user.isAdmin && <People />}
      <Agents />
      <Offline />
    </>
  );
}

function Shopping() {
  const memories = useStore((s) => Object.keys(s.memories).length);
  const [auto, setAuto] = useState(autoSwitchEnabled());
  return (
    <section className="card" aria-labelledby="h-shop">
      <h2 id="h-shop">Shopping</h2>
      <ul className="list-rows">
        <li><a className="grow row" style={{ textDecoration: 'none', color: 'inherit', minHeight: 44 }} {...linkProps('/stores')}><span className="grow">Stores, aisle order &amp; locations</span><ChevronRight size={20} aria-hidden /></a></li>
        <li><a className="grow row" style={{ textDecoration: 'none', color: 'inherit', minHeight: 44 }} {...linkProps('/remembered')}><span className="grow">Remembered items <span className="muted">({memories})</span></span><ChevronRight size={20} aria-hidden /></a></li>
        <li>
          <label className="switch grow">
            <input type="checkbox" checked={auto} onChange={(e) => { setAuto(e.target.checked); setAutoSwitch(e.target.checked); }} />
            <span>Switch to the store I’m in <span className="muted small" style={{ display: 'block' }}>Uses your location while the app is open. Set each store’s location under Stores.</span></span>
          </label>
        </li>
      </ul>
    </section>
  );
}

function Sections() {
  const categories = useStore((s) => s.categories);
  const toast = useToast();
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const list = orderedCategories(null, Object.values(categories));
  return (
    <section className="card" aria-labelledby="h-sections">
      <h2 id="h-sections">Sections</h2>
      <p className="muted small" style={{ marginTop: 0 }}>Rename any section; add your own. Each store’s order is set under Stores.</p>
      <ul className="list-rows">
        {list.map((c) => (
          <li key={c.id}>
            <label className="sr-only" htmlFor={`ce-${c.id}`}>Emoji for {c.name}</label>
            <input id={`ce-${c.id}`} defaultValue={c.emoji} style={{ width: 56, textAlign: 'center' }} maxLength={4}
              onBlur={(e) => { if (e.target.value !== c.emoji) updateCategory(c.id, { emoji: e.target.value }); }} />
            <label className="sr-only" htmlFor={`cn-${c.id}`}>Name of section {c.name}</label>
            <input id={`cn-${c.id}`} className="grow" style={{ flex: 1, width: 'auto' }} defaultValue={c.name}
              onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== c.name) updateCategory(c.id, { name: v }); else e.target.value = c.name; }} />
            {c.builtin ? <span className="tag">built in</span> : (
              <button className="icon-btn" aria-label={`Delete section ${c.name}`} onClick={() => {
                if (!window.confirm(`Delete ${c.name}? Its items move to Other.`)) return;
                deleteCategory(c.id);
                toast(`Deleted ${c.name}`);
              }}><Trash2 size={18} aria-hidden /></button>
            )}
          </li>
        ))}
      </ul>
      <form className="row" style={{ marginTop: 10 }} onSubmit={(e) => { e.preventDefault(); if (!name.trim()) return; addCategory(name, emoji.trim() || undefined); toast(`Added ${name.trim()}`); setName(''); setEmoji(''); }}>
        <label className="sr-only" htmlFor="new-cat-emoji">New section emoji</label>
        <input id="new-cat-emoji" value={emoji} onChange={(e) => setEmoji(e.target.value)} placeholder="🏷️" style={{ width: 56, textAlign: 'center' }} maxLength={4} />
        <label className="sr-only" htmlFor="new-cat">New section name</label>
        <input id="new-cat" value={name} onChange={(e) => setName(e.target.value)} placeholder="New section (Bulk bins…)" style={{ flex: 1, width: 'auto' }} />
        <button className="btn" disabled={!name.trim()}>Add section</button>
      </form>
    </section>
  );
}

function Account() {
  const { user, setUser, signOut } = useSession();
  const toast = useToast();
  const [name, setName] = useState(user.name);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  return (
    <section className="card" aria-labelledby="h-account">
      <h2 id="h-account">Account</h2>
      <p className="muted" style={{ marginTop: 0 }}>{user.email}{user.isAdmin ? ' · admin' : ''}</p>
      <form className="row" onSubmit={async (e) => { e.preventDefault(); try { setUser((await api.updateMe(name)).user); toast('Name saved'); } catch (err) { toast((err as Error).message, 'error'); } }}>
        <label className="sr-only" htmlFor="my-name">Your name</label>
        <input id="my-name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, width: 'auto' }} />
        <button className="btn" disabled={!name.trim() || name === user.name}>Save name</button>
      </form>
      <form className="stack" style={{ marginTop: 12 }} onSubmit={async (e) => {
        e.preventDefault();
        try { await api.changePassword(cur, next); setCur(''); setNext(''); toast('Password changed. Other devices were signed out.'); } catch (err) { toast((err as Error).message, 'error'); }
      }}>
        <label className="field">Current password<input type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} /></label>
        <label className="field">New password <span className="hint">8 or more characters</span><input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} /></label>
        <div className="row"><button className="btn" disabled={!cur || next.length < 8}>Change password</button><button type="button" className="btn ghost" onClick={() => void signOut()}>Sign out</button></div>
      </form>
    </section>
  );
}

function Passkeys() {
  const toast = useToast();
  const list = useLoad(() => api.passkeys(), []);
  const [busy, setBusy] = useState(false);
  if (!passkeysSupported()) return null;
  const add = async () => {
    setBusy(true);
    try { await registerPasskey(defaultPasskeyName()); toast('Passkey added'); list.reload(); }
    catch (e) { if (!wasCancelled(e)) toast((e as Error).message, 'error'); }
    finally { setBusy(false); }
  };
  return (
    <section className="card" aria-labelledby="h-passkeys">
      <h2 id="h-passkeys">Passkeys</h2>
      <p className="muted small" style={{ marginTop: 0 }}>Sign in with Face ID, Touch ID or 1Password instead of a password.</p>
      {list.loading && !list.data ? <Spinner /> : !!list.data?.length && (
        <ul className="list-rows">
          {list.data.map((p) => (
            <li key={p.id}>
              <span className="grow">{p.name}<span className="muted small"> · added {formatDate(p.createdAt)}</span></span>
              <button className="icon-btn" aria-label={`Remove passkey ${p.name}`} onClick={async () => {
                if (!window.confirm(`Remove “${p.name}”?`)) return;
                try { await api.deletePasskey(p.id); list.reload(); } catch (e) { toast((e as Error).message, 'error'); }
              }}><Trash2 size={18} aria-hidden /></button>
            </li>
          ))}
        </ul>
      )}
      <button className="btn" disabled={busy} onClick={() => void add()}><KeyRound size={18} aria-hidden /> {busy ? 'Waiting for your passkey…' : 'Add a passkey'}</button>
    </section>
  );
}

function People() {
  const toast = useToast();
  const { user } = useSession();
  const invites = useLoad(() => api.invites(), []);
  const users = useLoad(() => api.users(), []);
  const [email, setEmail] = useState('');
  const [link, setLink] = useState<string | null>(null);
  return (
    <section className="card" aria-labelledby="h-people">
      <h2 id="h-people">People</h2>
      <p className="muted small" style={{ marginTop: 0 }}>Everyone shares one list. Accounts are invite-only: a link works once and expires in 14 days.</p>
      <form className="row" onSubmit={async (e) => {
        e.preventDefault();
        try { const inv = await api.createInvite({ email: email.trim() || null }); setLink(inv.url); setEmail(''); invites.reload(); } catch (err) { toast((err as Error).message, 'error'); }
      }}>
        <label className="sr-only" htmlFor="inv-email">Their email (optional)</label>
        <input id="inv-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their email (optional)" style={{ flex: 1, width: 'auto' }} />
        <button className="btn primary">Create invite link</button>
      </form>
      {link && (
        <p className="row" style={{ marginTop: 10 }}>
          <code style={{ overflowWrap: 'anywhere', flex: 1 }}>{link}</code>
          <button className="icon-btn" aria-label="Copy invite link" onClick={async () => toast((await copyText(link)) ? 'Invite link copied' : 'Could not copy')}><Copy size={18} aria-hidden /></button>
        </p>
      )}
      {!!invites.data?.filter((i) => i.status === 'open').length && (
        <>
          <h3 style={{ marginTop: 14 }}>Open invites</h3>
          <ul className="list-rows">
            {invites.data.filter((i) => i.status === 'open').map((i) => (
              <li key={i.id}>
                <span className="grow">{i.email ?? 'Anyone with the link'}<span className="muted small"> · expires {formatDate(i.expiresAt)}</span></span>
                <button className="icon-btn" aria-label="Revoke invite" onClick={async () => { await api.deleteInvite(i.id); invites.reload(); }}><Trash2 size={18} aria-hidden /></button>
              </li>
            ))}
          </ul>
        </>
      )}
      <h3 style={{ marginTop: 14 }}>Members</h3>
      <ul className="list-rows">
        {users.data?.map((u) => (
          <li key={u.id}>
            <span className="grow"><strong>{u.name}</strong> {u.isAdmin && <span className="tag">admin</span>}<span className="muted small" style={{ display: 'block' }}>{u.email}</span></span>
            {u.id !== user.id && (
              <>
                <button className="btn ghost" onClick={async () => { await api.setAdmin(u.id, !u.isAdmin); users.reload(); }}>{u.isAdmin ? 'Remove admin' : 'Make admin'}</button>
                <button className="icon-btn" aria-label={`Remove ${u.name}`} onClick={async () => { if (window.confirm(`Remove ${u.name}? The list stays as it is.`)) { await api.deleteUser(u.id); users.reload(); } }}><Trash2 size={18} aria-hidden /></button>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Agents() {
  const toast = useToast();
  const tokens = useLoad(() => api.tokens(), []);
  const [name, setName] = useState('');
  const [fresh, setFresh] = useState<string | null>(null);
  const cmd = `claude mcp add --transport http milk-maybe ${webOrigin()}/mcp --header "Authorization: Bearer ${fresh ?? '<token>'}"`;
  return (
    <section className="card" aria-labelledby="h-agents">
      <h2 id="h-agents">Claude &amp; API tokens</h2>
      <p className="muted small" style={{ marginTop: 0 }}>Let Claude (or a Shortcut) read and add to the list. Claude connects over MCP at <code>{webOrigin()}/mcp</code>.</p>
      <form className="row" onSubmit={async (e) => {
        e.preventDefault();
        try { const t = await api.createToken(name.trim()); setFresh(t.token); setName(''); tokens.reload(); } catch (err) { toast((err as Error).message, 'error'); }
      }}>
        <label className="sr-only" htmlFor="tok-name">Token name</label>
        <input id="tok-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Token name (e.g. Claude)" style={{ flex: 1, width: 'auto' }} />
        <button className="btn" disabled={!name.trim()}>Create token</button>
      </form>
      {fresh && (
        <div style={{ marginTop: 10 }}>
          <p><strong>Copy this now</strong> — the token isn’t shown again.</p>
          <p className="row"><code style={{ overflowWrap: 'anywhere', flex: 1 }}>{cmd}</code>
            <button className="icon-btn" aria-label="Copy command" onClick={async () => toast((await copyText(cmd)) ? 'Copied' : 'Could not copy')}><Copy size={18} aria-hidden /></button></p>
        </div>
      )}
      {!!tokens.data?.length && (
        <ul className="list-rows">
          {tokens.data.map((t) => (
            <li key={t.id}>
              <span className="grow"><strong>{t.name}</strong><span className="muted small" style={{ display: 'block' }}>Created {formatDate(t.createdAt)} · last used {formatDate(t.lastUsedAt)}</span></span>
              <button className="icon-btn" aria-label={`Delete token ${t.name}`} onClick={async () => { if (window.confirm(`Delete “${t.name}”?`)) { await api.deleteToken(t.id); tokens.reload(); } }}><Trash2 size={18} aria-hidden /></button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Offline() {
  const online = useStore((s) => s.online);
  const lastSync = useStore((s) => s.lastSync);
  const queued = useStore((s) => s.queued);
  const syncing = useStore((s) => s.syncing);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (location.hash === '#offline') document.getElementById('offline')?.scrollIntoView(); }, []);
  return (
    <section className="card" id="offline" aria-labelledby="h-offline">
      <h2 id="h-offline">Offline &amp; sync</h2>
      <p className="muted small" style={{ marginTop: 0 }}>The whole list lives on this device, so it works in a store with no signal. Changes are sent when you’re back online.</p>
      <p>{online ? 'Connected.' : <strong>Offline.</strong>} {lastSync ? `Last synced ${new Date(lastSync).toLocaleString()}.` : 'Not synced yet.'}</p>
      {queued.length > 0 && (
        <>
          <h3>Waiting to send ({queued.length})</h3>
          <ul>{queued.slice(0, 20).map((q, i) => <li key={i}>{q}</li>)}</ul>
        </>
      )}
      <button className="btn" disabled={busy} onClick={async () => { setBusy(true); try { await syncNow({ pull: true, timeoutMs: 20_000 }); } finally { setBusy(false); } }}>
        <RefreshCw size={16} aria-hidden className={busy || syncing ? 'spin' : ''} /> Sync now
      </button>
    </section>
  );
}
